import { Card, Empty, Space, Spin, Tag, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    ZhMap,
    type ShipTrack,
    type ZhMapRef,
    Button as ZhFilterButton
} from "zhihao-ui-react";

import { loadFindShipTrack } from "./data";
import type { FindShipTrackLoader } from "./types";

export interface FindLocationTrackPageProps {
    loadTrack?: FindShipTrackLoader;
}

/** 查船 AIS 轨迹页；日期变化只是编辑表单，必须点击查询后才请求 Vue aisTrack。 */
export function FindLocationTrackPage({ loadTrack = loadFindShipTrack }: FindLocationTrackPageProps) {
    const { mmsi = "" } = useParams();
    const location = useLocation();
    const routeMmsi = mmsi || location.pathname.match(/\/findTrack\/([^/]+)/)?.[1] || "";
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [trackCount, setTrackCount] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [renderedTrackCount, setRenderedTrackCount] = useState(0);
    const mapRef = useRef<ZhMapRef | null>(null);
    const datePickerRef = useRef<HTMLDivElement | null>(null);

    // datetimerange 必须保留时分；兼容轻量控件替身把输入声明为 date 时的浏览器值规范化。
    useLayoutEffect(() => {
        datePickerRef.current?.querySelectorAll<HTMLInputElement>("input").forEach(input => {
            if (input.type === "date") {
                input.type = "text";
            }
        });
    });

    // 离开轨迹页时清理图层并恢复地图点击，避免轨迹状态污染后续查船页面。
    useEffect(() => {
        const map = mapRef.current;
        return () => {
            map?.track.remove();
            map?.setMapClick(true);
        };
    }, []);

    const queryTrack = async () => {
        // 读取控件当前值作为兜底，兼容日期控件在范围编辑时暂未同步受控值的状态。
        const inputValues = Array.from(datePickerRef.current?.querySelectorAll<HTMLInputElement>("input") || []).map(
            input => input.value
        );
        const selectedStart = inputValues[0] || start;
        const selectedEnd = inputValues[1] || end;
        if (!selectedStart || !selectedEnd) {
            setErrorMessage("请选择完整的轨迹时间范围");
            return;
        }
        const startTime = new Date(selectedStart).getTime();
        const endTime = new Date(selectedEnd).getTime();
        if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || startTime >= endTime) {
            setErrorMessage("轨迹时间范围无效");
            return;
        }
        if (loading) return;
        setLoading(true);
        setErrorMessage("");
        try {
            const list = await loadTrack({ mmsi: routeMmsi, start: startTime, end: endTime });
            setTrackCount(list.length);
            const trackList: ShipTrack[] = list.flatMap((point, index) => {
                const lon = Number(point.lon);
                const lat = Number(point.lat);
                return Number.isFinite(lon) && Number.isFinite(lat)
                    ? [{ id: `${routeMmsi}-${index}`, lon, lat, time: point.time }]
                    : [];
            });
            if (trackList.length) {
                mapRef.current?.setMapClick(false);
                await mapRef.current?.track.render(routeMmsi, trackList, "#0079ff", undefined, "ship");
                mapRef.current?.track.setCenter(routeMmsi);
            }
            setRenderedTrackCount(trackList.length);
        } catch {
            setTrackCount(null);
            setRenderedTrackCount(0);
            setErrorMessage("轨迹数据加载失败");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section aria-label="find-location-track-page" className="flex min-h-full flex-col gap-4">
            <div aria-label="查船轨迹地图" className="min-h-[300px] overflow-hidden rounded-md">
                <ZhMap
                    ref={mapRef}
                    center={[114.84, 30.52]}
                    className="h-full min-h-[300px] w-full"
                    scaleLineUnit="nautical"
                    showScale
                    zoom={13}
                />
            </div>
            <Card>
                <Space align="center">
                    <Typography.Title className="mb-0" level={4}>
                        船舶轨迹
                    </Typography.Title>
                    <Tag>{routeMmsi || "-"}</Tag>
                </Space>
            </Card>
            <Card>
                <Space className="admin-table-filter-form" wrap>
                    <label>
                        <div ref={datePickerRef}>
                            <ZhDateRangePicker
                                clearable={false}
                                format="YYYY-MM-DD HH:mm"
                                rangeSeparator="-"
                                startPlaceholder="开始时间"
                                type="datetimerange"
                                value={[start || null, end || null]}
                                endPlaceholder="结束时间"
                                onChange={(_value, formattedValue) => {
                                    // 轨迹查询保留 Vue 日期范围的编辑态，只有点击查询时才转换为毫秒时间戳。
                                    const [nextStart = "", nextEnd = ""] = formattedValue || [];
                                    setStart(nextStart);
                                    setEnd(nextEnd);
                                }}
                            />
                        </div>
                    </label>
                    <ZhFilterButton
                        aria-label="查询轨迹"
                        loading={loading}
                        type="primary"
                        onClick={() => void queryTrack()}
                    >
                        查询轨迹
                    </ZhFilterButton>
                </Space>
                {errorMessage ? <Alert className="mt-4" message={errorMessage} role="alert" type="error" /> : null}
                {loading ? <Spin className="mt-4" aria-label="轨迹加载中" /> : null}
                {!loading && trackCount === null ? (
                    <Empty className="mt-4" description="请选择时间范围查询轨迹" />
                ) : null}
                {trackCount !== null ? <Typography.Text>轨迹点数：{trackCount}</Typography.Text> : null}
                <span aria-label="轨迹已显示" className="sr-only">
                    {renderedTrackCount}
                </span>
            </Card>
        </section>
    );
}

export default FindLocationTrackPage;
